import type { GetStaticPathsContext, GetStaticPropsContext, InferGetStaticPropsType } from "next"
import { useRouter } from "next/router"
import { Layout } from "@components/common"
import { BuilderComponent, Builder, builder } from "@builder.io/react"
import builderConfig from "@config/builder"
import { NextSeo } from "next-seo"
import Link from "next/link"
import Head from "next/head"
import { useThemeUI } from "theme-ui"
import { getLayoutProps } from "@lib/get-layout-props"
import { classNames } from "core/utils"
import DefaultErrorPage from "next/error"
import { NoSSR } from "@components/common"
Builder.isStatic = true

const builderModel = "blog-post"

// disqus comments
import { DiscussionEmbed } from "disqus-react"
const DisqusComments = ({ post }: { post: any }) => {
  const disqusShortname = "newwayvan-life"
  const disqusConfig = {
    url: location.protocol + "//" + location.host + location.pathname,
    identifier: post.id, // Single post id
    title: post.title, // Single post title
  }
  return (
    <div className="mx-auto max-w-3xl">
      <DiscussionEmbed shortname={disqusShortname} config={disqusConfig} />
    </div>
  )
}

// Breadcrumb
import { HomeIcon } from "@heroicons/react/20/solid"

const pages = [{ name: "Blog", href: "/blog", current: false }]

export function Breadcrumb() {
  return (
    <nav className="flex" aria-label="Breadcrumb">
      <ol role="list" className="flex items-center space-x-4">
        <li>
          <div>
            <Link href="/" className="text-gray-400 hover:text-gray-500">
              <div>
                {" "}
                <HomeIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                <span className="sr-only">Home</span>
              </div>
            </Link>
          </div>
        </li>
        {pages.map((page) => (
          <li key={page.name}>
            <div className="flex items-center">
              <svg
                className="h-5 w-5 shrink-0 text-gray-300"
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
              </svg>
              <Link
                href={page.href}
                className="ml-4 text-sm font-medium text-gray-500 hover:text-gray-700"
                aria-current={page.current ? "page" : undefined}
              >
                {page.name}
              </Link>
            </div>
          </li>
        ))}
      </ol>
    </nav>
  )
}

export default function BlogArticle({ article }: { article: any }) {
  Builder.isStatic = true
  const router = useRouter()
  const isLive = !Builder.isEditing && !Builder.isPreviewing
  const { theme } = useThemeUI()

  if (router.isFallback) {
    return (
      <>
        <Head>
          <meta name="title">what the actual</meta>
        </Head>
        <h1>Loading...</h1>
      </>
    )
  }
  // This includes setting the noindex header because static files always return a status 200 but the rendered not found page page should obviously not be indexed
  if (!article && !Builder.isEditing && !Builder.isPreviewing) {
    return (
      <>
        <Head>
          <meta name="robots" content="noindex" />
          <meta name="title"></meta>
        </Head>
        {Builder.isBrowser && <DefaultErrorPage statusCode={404} />}
      </>
    )
  }

  return (
    <div>
      {!article ? (
        <>
          <div className="p-2 text-center">
            Article not found. <Link href="/blog">Back to the blog</Link>
          </div>
        </>
      ) : (
        <>
          <NextSeo
            title={article.data.title}
            description={article.data.blurb}
            openGraph={{
              type: "website",
              title: article.data.title,
              description: article.data.blurb,
              images: [
                {
                  url: article.data.image,
                  width: 800,
                  height: 600,
                  alt: article.data.title,
                },
              ],
            }}
          />

          <div>
            <div className="mx-auto mt-4 max-w-7xl">
              <Breadcrumb />
            </div>

            <div className="relative overflow-hidden bg-white py-16">
              <div className="hidden lg:absolute lg:inset-y-0 lg:block lg:h-full lg:w-full">
                <div className="relative mx-auto h-full max-w-prose text-lg" aria-hidden="true">
                  <svg
                    className="absolute left-full top-12 translate-x-32 transform"
                    width={404}
                    height={384}
                    fill="none"
                    viewBox="0 0 404 384"
                  >
                    <defs>
                      <pattern
                        id="74b3fd99-0a6f-4271-bef2-e80eeafdf357"
                        x={0}
                        y={0}
                        width={20}
                        height={20}
                        patternUnits="userSpaceOnUse"
                      >
                        <rect
                          x={0}
                          y={0}
                          width={4}
                          height={4}
                          className="text-gray-200"
                          fill="currentColor"
                        />
                      </pattern>
                    </defs>
                    <rect
                      width={404}
                      height={384}
                      fill="url(#74b3fd99-0a6f-4271-bef2-e80eeafdf357)"
                    />
                  </svg>
                  <svg
                    className="absolute right-full top-1/2 -translate-x-32 -translate-y-1/2 transform"
                    width={404}
                    height={384}
                    fill="none"
                    viewBox="0 0 404 384"
                  >
                    <defs>
                      <pattern
                        id="f210dbf6-a58d-4871-961e-36d5016a0f49"
                        x={0}
                        y={0}
                        width={20}
                        height={20}
                        patternUnits="userSpaceOnUse"
                      >
                        <rect
                          x={0}
                          y={0}
                          width={4}
                          height={4}
                          className="text-gray-200"
                          fill="currentColor"
                        />
                      </pattern>
                    </defs>
                    <rect
                      width={404}
                      height={384}
                      fill="url(#f210dbf6-a58d-4871-961e-36d5016a0f49)"
                    />
                  </svg>
                  <svg
                    className="absolute bottom-12 left-full translate-x-32 transform"
                    width={404}
                    height={384}
                    fill="none"
                    viewBox="0 0 404 384"
                  >
                    <defs>
                      <pattern
                        id="d3eb07ae-5182-43e6-857d-35c643af9034"
                        x={0}
                        y={0}
                        width={20}
                        height={20}
                        patternUnits="userSpaceOnUse"
                      >
                        <rect
                          x={0}
                          y={0}
                          width={4}
                          height={4}
                          className="text-gray-200"
                          fill="currentColor"
                        />
                      </pattern>
                    </defs>
                    <rect
                      width={404}
                      height={384}
                      fill="url(#d3eb07ae-5182-43e6-857d-35c643af9034)"
                    />
                  </svg>
                </div>
              </div>

              <div className="relative px-4 sm:px-6 lg:px-8">
                <div className="mx-auto max-w-prose text-lg">
                  <h1>
                    <span
                      className={classNames(
                        article.data.tagline ? "block" : "hidden",
                        "text-primary-600 text-center text-base font-semibold uppercase tracking-wide"
                      )}
                    >
                      {article.data.tagline}
                    </span>
                    <span className="mt-2 block text-center text-3xl font-extrabold leading-8 tracking-tight text-gray-900 sm:text-4xl">
                      {article.data.title}
                    </span>
                  </h1>
                  <p className="mt-8 text-xl leading-8 text-gray-500">{article.data.blurb}</p>
                </div>
                <div
                  className="prose-primary prose prose-lg mx-auto mt-6 text-gray-500"
                  dangerouslySetInnerHTML={{ __html: article.data.content }}
                />
              </div>
            </div>
          </div>
          <NoSSR>
            <DisqusComments post={article.data} />
          </NoSSR>
        </>
      )}
    </div>
  )
}
export async function getStaticProps(context: GetStaticPropsContext<{ handle: string }>) {
  const article = await builder
    .get("blog-post", {
      // Include references, like our `author` ref
      options: { enrich: true },
      query: {
        // Get the specific article by handle
        "data.handle": context.params?.handle,
      },
    })
    .promise()
  const theme = (await builder.get("theme").promise()) || null

  return { props: { article: article || null, theme }, revalidate: 30 }
}

export async function getStaticPaths({ locales }: GetStaticPathsContext) {
  const posts = await builder.getAll("blog-post", {
    fields: "data.handle",
  })

  return {
    paths: posts?.map((post: any) => `/blog/${post.data.handle}`) ?? [],
    fallback: "blocking",
  }
}

BlogArticle.Layout = Layout
